/**
 * TarefasBlock — checklist de tarefas + subtarefas do lead.
 *
 * Cada tarefa tem checkbox (toggle), prioridade + vencimento, edicao inline
 * (titulo/prioridade/vencimento), subtarefas expansiveis com checkbox +
 * edicao de titulo, e botoes adicionar subtarefa / excluir.
 *
 * Estado vazio usa o EmptyState compartilhado. O form de nova tarefa fica
 * escondido por tras do botao "Adicionar tarefa" (revela ao clicar).
 *
 * Os toggles/edicoes usam callbacks que recarregam o lead no pai; aqui
 * mantemos so o estado de UI (form aberto, modos de edicao, inputs).
 *
 * `podeEditar` (lead:edit) governa toda escrita — criar, editar, concluir e
 * excluir tarefa ou subtarefa exigem lead:edit no backend (CRM-23). Sem a
 * permissao o bloco e so leitura: checkbox desabilitado e sem botoes.
 */
import React, { useState } from "react";
import { FiCheck, FiEdit2, FiPlus, FiTrash2, FiX } from "react-icons/fi";

import Block from "../../../components/Block";
import AsyncButton from "../../../components/AsyncButton";
import useResponsaveisCrm from "../../../hooks/useResponsaveisCrm";
import { EmptyState } from "../../../components/EmptyState";

import {
  AddRow,
  CheckLabel,
  CheckRow,
  MiniButton,
  SubLabel,
  SubLista,
  SubRow,
  TarefaItem,
} from "./styles";
import { Field, FormStack, InlineError, Input, Select } from "../styles";

const PRIORIDADES = [
  { value: "BAIXA", label: "Baixa", tone: "neutral" },
  { value: "MEDIA", label: "Média", tone: "info" },
  { value: "ALTA", label: "Alta", tone: "warning" },
];

const PRIORIDADE_TONE = PRIORIDADES.reduce((acc, p) => {
  acc[p.value] = p.tone;
  return acc;
}, {});

function fmtVenc(iso) {
  if (!iso) return null;
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString("pt-BR");
  } catch {
    return null;
  }
}

function venceu(iso) {
  if (!iso) return false;
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  return new Date(`${iso}T00:00:00`) < hoje;
}

// Linha de subtarefa com edicao inline de titulo.
function SubtarefaLinha({
  sub,
  onToggleSubtarefa,
  onAtualizarSubtarefa,
  onExcluirSubtarefa,
  podeEditar,
}) {
  const [editando, setEditando] = useState(false);
  const [titulo, setTitulo] = useState(sub.titulo || "");

  const salvar = async () => {
    const t = titulo.trim();
    if (!t) return; // botao desabilitado quando vazio
    await onAtualizarSubtarefa(sub.id, { titulo: t });
    setEditando(false);
  };

  if (editando && podeEditar) {
    return (
      <AddRow>
        <Input
          type="text"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              salvar().catch(() => {});
            }
          }}
          maxLength={200}
          autoFocus
          style={{ flex: 1 }}
        />
        <AsyncButton
          variant="secondary"
          onClick={salvar}
          successMessage="Subtarefa atualizada."
          silentError
          disabled={!titulo.trim()}
        >
          <FiCheck size={12} aria-hidden="true" /> Salvar
        </AsyncButton>
        <MiniButton
          type="button"
          onClick={() => {
            setEditando(false);
            setTitulo(sub.titulo || "");
          }}
        >
          Cancelar
        </MiniButton>
      </AddRow>
    );
  }

  return (
    <SubRow>
      <input
        type="checkbox"
        checked={Boolean(sub.concluida)}
        onChange={(e) => onToggleSubtarefa(sub.id, e.target.checked)}
        disabled={!podeEditar}
        aria-label={`Concluir subtarefa ${sub.titulo}`}
      />
      <SubLabel $done={sub.concluida}>{sub.titulo}</SubLabel>
      {podeEditar && (
        <>
          <MiniButton
            type="button"
            onClick={() => setEditando(true)}
            title="Editar subtarefa"
          >
            <FiEdit2 size={11} aria-hidden="true" />
          </MiniButton>
          <MiniButton
            type="button"
            $tone="danger"
            onClick={() => onExcluirSubtarefa(sub.id)}
            title="Excluir subtarefa"
          >
            <FiTrash2 size={11} aria-hidden="true" />
          </MiniButton>
        </>
      )}
    </SubRow>
  );
}

function TarefaLinha({
  tarefa,
  onToggleTarefa,
  onAtualizarTarefa,
  onExcluirTarefa,
  onCriarSubtarefa,
  onToggleSubtarefa,
  onAtualizarSubtarefa,
  onExcluirSubtarefa,
  podeEditar,
}) {
  const [novaSub, setNovaSub] = useState("");
  const [adicionandoSub, setAdicionandoSub] = useState(false);
  const [editando, setEditando] = useState(false);
  const [editTitulo, setEditTitulo] = useState(tarefa.titulo || "");
  const [editPrioridade, setEditPrioridade] = useState(
    tarefa.prioridade || "MEDIA"
  );
  const [editVencimento, setEditVencimento] = useState(
    tarefa.data_vencimento || ""
  );
  const [erroEdit, setErroEdit] = useState(null);

  const subtarefas = Array.isArray(tarefa.subtarefas) ? tarefa.subtarefas : [];
  const venc = fmtVenc(tarefa.data_vencimento);
  const atrasada = !tarefa.concluida && venceu(tarefa.data_vencimento);

  const adicionarSub = async () => {
    const t = novaSub.trim();
    if (!t) return; // botao desabilitado quando vazio
    await onCriarSubtarefa(tarefa.id, t);
    setNovaSub("");
    setAdicionandoSub(false);
  };

  const abrirEdicao = () => {
    setEditTitulo(tarefa.titulo || "");
    setEditPrioridade(tarefa.prioridade || "MEDIA");
    setEditVencimento(tarefa.data_vencimento || "");
    setErroEdit(null);
    setEditando(true);
  };

  const salvarEdicao = async () => {
    setErroEdit(null);
    const t = editTitulo.trim();
    if (!t) {
      setErroEdit("Informe o título da tarefa.");
      throw new Error("Título obrigatório.");
    }
    await onAtualizarTarefa(tarefa.id, {
      titulo: t,
      prioridade: editPrioridade,
      data_vencimento: editVencimento || null,
    });
    setEditando(false);
  };

  // ---------- Modo edicao da tarefa ----------
  if (editando && podeEditar) {
    return (
      <TarefaItem>
        <FormStack>
          <Field>
            Título
            <Input
              type="text"
              value={editTitulo}
              onChange={(e) => setEditTitulo(e.target.value)}
              maxLength={200}
              autoFocus
            />
          </Field>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Field style={{ flex: 1, minWidth: 120 }}>
              Prioridade
              <Select
                value={editPrioridade}
                onChange={(e) => setEditPrioridade(e.target.value)}
              >
                {PRIORIDADES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field style={{ flex: 1, minWidth: 120 }}>
              Vencimento
              <Input
                type="date"
                value={editVencimento}
                onChange={(e) => setEditVencimento(e.target.value)}
              />
            </Field>
          </div>
          {erroEdit && <InlineError>{erroEdit}</InlineError>}
          <div style={{ display: "flex", gap: 6 }}>
            <AsyncButton
              variant="primary"
              onClick={salvarEdicao}
              successMessage="Tarefa atualizada."
              silentError
            >
              <FiCheck size={13} aria-hidden="true" /> Salvar
            </AsyncButton>
            <MiniButton type="button" onClick={() => setEditando(false)}>
              Cancelar
            </MiniButton>
          </div>
        </FormStack>
      </TarefaItem>
    );
  }

  // ---------- Modo leitura ----------
  return (
    <TarefaItem>
      <CheckRow>
        <input
          type="checkbox"
          checked={Boolean(tarefa.concluida)}
          onChange={(e) => onToggleTarefa(tarefa.id, e.target.checked)}
          disabled={!podeEditar}
          aria-label={`Concluir tarefa ${tarefa.titulo}`}
          style={{ marginTop: 3 }}
        />
        <CheckLabel $done={tarefa.concluida}>{tarefa.titulo}</CheckLabel>
        <span
          className={`badge ${PRIORIDADE_TONE[tarefa.prioridade] || "neutral"} no-dot`}
        >
          {tarefa.prioridade_display || tarefa.prioridade}
        </span>
        {venc && (
          <span className={`badge ${atrasada ? "danger" : "neutral"} no-dot`}>
            {atrasada ? "Venceu" : "Vence"} {venc}
          </span>
        )}
        {podeEditar && (
          <>
            <MiniButton
              type="button"
              onClick={abrirEdicao}
              title="Editar tarefa"
            >
              <FiEdit2 size={12} aria-hidden="true" />
            </MiniButton>
            <MiniButton
              type="button"
              $tone="danger"
              onClick={() => onExcluirTarefa(tarefa.id)}
              title="Excluir tarefa"
            >
              <FiTrash2 size={12} aria-hidden="true" />
            </MiniButton>
          </>
        )}
      </CheckRow>

      {subtarefas.length > 0 && (
        <SubLista>
          {subtarefas.map((s) => (
            <SubtarefaLinha
              key={s.id}
              sub={s}
              onToggleSubtarefa={onToggleSubtarefa}
              onAtualizarSubtarefa={onAtualizarSubtarefa}
              onExcluirSubtarefa={onExcluirSubtarefa}
              podeEditar={podeEditar}
            />
          ))}
        </SubLista>
      )}

      {podeEditar &&
        (adicionandoSub ? (
          <AddRow style={{ marginLeft: 26 }}>
            <Input
              type="text"
              value={novaSub}
              onChange={(e) => setNovaSub(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  // .catch para nao virar unhandled rejection fora do AsyncButton.
                  adicionarSub().catch(() => {});
                }
              }}
              placeholder="Título da subtarefa"
              maxLength={200}
              autoFocus
              style={{ flex: 1 }}
            />
            <AsyncButton
              variant="secondary"
              onClick={adicionarSub}
              successMessage="Subtarefa adicionada."
              silentError
              disabled={!novaSub.trim()}
            >
              Add
            </AsyncButton>
            <MiniButton type="button" onClick={() => setAdicionandoSub(false)}>
              Cancelar
            </MiniButton>
          </AddRow>
        ) : (
          <MiniButton
            type="button"
            onClick={() => setAdicionandoSub(true)}
            style={{ marginLeft: 26 }}
          >
            <FiPlus size={12} aria-hidden="true" /> Subtarefa
          </MiniButton>
        ))}
    </TarefaItem>
  );
}

function TarefasBlock({
  tarefas,
  onCriarTarefa,
  onToggleTarefa,
  onAtualizarTarefa,
  onExcluirTarefa,
  onCriarSubtarefa,
  onToggleSubtarefa,
  onAtualizarSubtarefa,
  onExcluirSubtarefa,
  podeEditar = false,
  collapsible,
  responsavelPadrao = "",
  defaultCollapsed,
}) {
  const lista = Array.isArray(tarefas) ? tarefas : [];
  const abertas = lista.filter((t) => !t.concluida).length;

  const [formAberto, setFormAberto] = useState(false);
  const [titulo, setTitulo] = useState("");
  const [prioridade, setPrioridade] = useState("MEDIA");
  const [vencimento, setVencimento] = useState("");
  // CRM-25: a tarefa nascia sem dono e nao chegava ao sino de ninguem.
  // Padrao: o responsavel do lead; a lista vem do endpoint do CRM (CRM-22).
  const [responsavel, setResponsavel] = useState(responsavelPadrao || "");
  const { responsaveis } = useResponsaveisCrm({ enabled: Boolean(podeEditar) && formAberto });
  const [erro, setErro] = useState(null);

  const resetForm = () => {
    setTitulo("");
    setPrioridade("MEDIA");
    setVencimento("");
    setResponsavel(responsavelPadrao || "");
    setErro(null);
  };

  const criar = async () => {
    setErro(null);
    const t = titulo.trim();
    if (!t) {
      setErro("Informe o título da tarefa.");
      throw new Error("Título obrigatório.");
    }
    const dados = { titulo: t, prioridade };
    if (vencimento) dados.data_vencimento = vencimento;
    if (responsavel) dados.responsavel = responsavel;
    await onCriarTarefa(dados);
    resetForm();
    setFormAberto(false);
  };

  return (
    <Block
      title="Tarefas"
      subtitle={lista.length ? `${abertas} aberta(s) de ${lista.length}` : undefined}
      collapsible={collapsible}
      defaultCollapsed={defaultCollapsed}
    >
      {lista.length === 0 ? (
        <EmptyState
          tone="info"
          titulo="Nenhuma tarefa"
          descricao={
            podeEditar
              ? "Crie tarefas para organizar os próximos passos deste lead."
              : "Nenhuma tarefa registrada para este lead."
          }
        />
      ) : (
        lista.map((t) => (
          <TarefaLinha
            key={t.id}
            tarefa={t}
            onToggleTarefa={onToggleTarefa}
            onAtualizarTarefa={onAtualizarTarefa}
            onExcluirTarefa={onExcluirTarefa}
            onCriarSubtarefa={onCriarSubtarefa}
            onToggleSubtarefa={onToggleSubtarefa}
            onAtualizarSubtarefa={onAtualizarSubtarefa}
            onExcluirSubtarefa={onExcluirSubtarefa}
            podeEditar={podeEditar}
          />
        ))
      )}

      {podeEditar && (
        <div style={{ paddingTop: 10, borderTop: "1px solid var(--divider)" }}>
          {formAberto ? (
            <FormStack>
              <Field>
                Nova tarefa
                <Input
                  type="text"
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      // criar() faz throw em titulo vazio (mostra InlineError);
                      // .catch evita unhandled rejection fora do AsyncButton.
                      criar().catch(() => {});
                    }
                  }}
                  placeholder="Ex: Enviar render da cozinha"
                  maxLength={200}
                  autoFocus
                />
              </Field>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Field style={{ flex: 1, minWidth: 120 }}>
                  Prioridade
                  <Select
                    value={prioridade}
                    onChange={(e) => setPrioridade(e.target.value)}
                  >
                    {PRIORIDADES.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field style={{ flex: 1, minWidth: 120 }}>
                  Vencimento
                  <Input
                    type="date"
                    value={vencimento}
                    onChange={(e) => setVencimento(e.target.value)}
                  />
                </Field>
              </div>
              <Field>
                Responsável
                <Select
                  value={responsavel}
                  onChange={(e) => setResponsavel(e.target.value)}
                >
                  <option value="">— O responsável do lead —</option>
                  {responsaveis.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nome}
                    </option>
                  ))}
                </Select>
              </Field>
              {erro && <InlineError>{erro}</InlineError>}
              <div style={{ display: "flex", gap: 6 }}>
                <AsyncButton
                  variant="primary"
                  onClick={criar}
                  successMessage="Tarefa criada."
                  silentError
                >
                  <FiPlus size={14} aria-hidden="true" /> Adicionar
                </AsyncButton>
                <MiniButton
                  type="button"
                  onClick={() => {
                    resetForm();
                    setFormAberto(false);
                  }}
                >
                  <FiX size={13} aria-hidden="true" /> Cancelar
                </MiniButton>
              </div>
            </FormStack>
          ) : (
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => setFormAberto(true)}
            >
              <FiPlus size={14} aria-hidden="true" /> Adicionar tarefa
            </button>
          )}
        </div>
      )}
    </Block>
  );
}

export default TarefasBlock;
